import {
  Directive,
  ElementRef,
  inject,
  OnInit,
  OnDestroy,
  DestroyRef,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, merge } from 'rxjs';
import { SwipeNavigationService } from '../../core/services/swipe-navigation.service';

/**
 * Swipe Navigation Directive
 *
 * Detects horizontal swipe gestures and triggers navigation between screens.
 * Apply to the main content area to enable swipe navigation.
 *
 * @example
 * ```html
 * <main appSwipeNavigation class="flex-1">
 *   <router-outlet />
 * </main>
 * ```
 */
@Directive({
  selector: '[appSwipeNavigation]',
  standalone: true,
})
export class SwipeNavigationDirective implements OnInit, OnDestroy {
  private readonly el = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly swipeService = inject(SwipeNavigationService);

  /**
   * Minimum horizontal distance (in pixels) to trigger a swipe
   */
  readonly swipeThreshold = input<number>(80);

  /**
   * Maximum vertical distance (in pixels) allowed during horizontal swipe
   * If exceeded, it's considered a vertical scroll, not a horizontal swipe
   */
  readonly maxVerticalDistance = input<number>(100);

  /**
   * Minimum swipe velocity (pixels/ms) to trigger navigation
   * Allows quick short swipes to also trigger navigation
   */
  readonly minVelocity = input<number>(0.3);

  /**
   * Whether to enable swipe navigation on this element
   */
  readonly swipeEnabled = input<boolean>(true);

  /**
   * CSS selector for areas where swipe should be ignored (e.g., carousels)
   */
  readonly ignoreSelector = input<string | undefined>(undefined);

  /**
   * CSS selector for containers where swipe IS allowed.
   * If provided, swipe only works when touch starts within these containers.
   * Example: '.offers-background, .menus-background, .places-container, .favorites-container'
   */
  readonly allowedContainers = input<string | undefined>(undefined);

  // Touch tracking
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private lastTouchX = 0;
  private lastTouchTime = 0;
  private isSwiping = false;
  /** Whether we've determined this is a horizontal swipe (for preventing default) */
  private isHorizontalGesture = false;

  ngOnInit(): void {
    this.setupTouchHandlers();
  }

  ngOnDestroy(): void {
    // Cleanup handled by takeUntilDestroyed
  }

  /**
   * Set up touch event handlers for swipe detection
   */
  private setupTouchHandlers(): void {
    const element = this.el.nativeElement as HTMLElement;

    const touchStart$ = fromEvent<TouchEvent>(element, 'touchstart', {
      passive: true,
    });
    // touchmove must be non-passive to allow preventDefault for blocking native gestures
    const touchMove$ = fromEvent<TouchEvent>(element, 'touchmove', {
      passive: false,
    });
    const touchEnd$ = fromEvent<TouchEvent>(element, 'touchend', {
      passive: true,
    });
    const touchCancel$ = fromEvent<TouchEvent>(element, 'touchcancel', {
      passive: true,
    });

    // Touch start - record initial position
    touchStart$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (!this.swipeEnabled() || !this.swipeService.enabled()) {
        return;
      }

      const target = event.target as HTMLElement;

      // Check if we should ignore this touch (e.g., in a carousel)
      const ignoreSelector = this.ignoreSelector();
      if (ignoreSelector) {
        const ignoredElement = target.closest(ignoreSelector);
        if (ignoredElement) {
          return;
        }
      }

      // Check if touch is within an allowed container (if specified)
      const allowedContainers = this.allowedContainers();
      if (allowedContainers) {
        const allowedElement = target.closest(allowedContainers);
        if (!allowedElement) {
          return; // Touch not within allowed container
        }
      }

      // Check if we're on a navigable screen (route check)
      if (!this.swipeService.isNavigableScreen()) {
        return;
      }

      const now = Date.now();
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.touchStartTime = now;
      this.lastTouchX = this.touchStartX;
      this.lastTouchTime = now;
      this.isSwiping = true;
      this.isHorizontalGesture = false; // Reset on new gesture
      this.swipeService.startSwipeGesture(); // Reset navigation tracking
    });

    // Touch move - track gesture and trigger navigation with fluid threshold
    touchMove$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (!this.isSwiping) return;

      const now = Date.now();
      const touchX = event.touches[0].clientX;
      const touchY = event.touches[0].clientY;
      const deltaX = touchX - this.touchStartX;
      const deltaY = Math.abs(touchY - this.touchStartY);
      const absDeltaX = Math.abs(deltaX);

      // Calculate instantaneous velocity (more responsive than average velocity)
      const timeDelta = now - this.lastTouchTime;
      const velocity =
        timeDelta > 0 ? Math.abs(touchX - this.lastTouchX) / timeDelta : 0;

      // Update tracking for next velocity calculation
      this.lastTouchX = touchX;
      this.lastTouchTime = now;

      // Determine gesture direction once we have enough movement
      if (!this.isHorizontalGesture && (absDeltaX > 10 || deltaY > 10)) {
        // If horizontal movement is greater, this is a horizontal swipe
        this.isHorizontalGesture = absDeltaX > deltaY;

        // If vertical movement exceeds threshold, cancel swipe detection
        if (deltaY > this.maxVerticalDistance()) {
          this.isSwiping = false;
          this.isHorizontalGesture = false;
          return;
        }
      }

      // For horizontal gestures, try to trigger navigation with fluid approach
      if (this.isHorizontalGesture) {
        // Prevent native browser gestures (like back swipe)
        if (event.cancelable) {
          event.preventDefault();
        }

        // Fluid navigation: fast swipes (0.5+ px/ms) trigger at 30px,
        // slow drags need the full threshold (80px default)
        this.swipeService.tryFluidNavigation(
          deltaX,
          velocity,
          this.swipeThreshold(),
          this.minVelocity(),
        );
      }
    });

    // Touch end - cleanup and handle any remaining navigation
    merge(touchEnd$, touchCancel$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isSwiping) return;

        this.isSwiping = false;
        this.isHorizontalGesture = false;
        this.swipeService.endSwipeGesture();
      });
  }
}
